/**
 * ProcessApprovalBanner — odchudzony baner akceptacji nad zakładkami (F17 T17.1.8b).
 *
 * Jedna linia: z lewej poziomy stepper postępu (`ApprovalProgressMini`), z prawej
 * zwarte przyciski akcji. Na wąskim ekranie (poniżej `sm`, np. telefon) pasek układa się w dwie
 * linie: postęp u góry, przyciski pod nim — zawijane w kolejne linie, gdy się nie mieszczą.
 * Gęsta `ProcessStepsTable` NIE jest już tu renderowana —
 * pełna historia kroków jest w `ProcessHistoryTab` (miejsca na kartach — nagłówek tamtego pliku).
 *
 * Model „slotu" (T17.1.8b): rekord ma najwyżej jedną zajmującą instancję.
 *   - slot WOLNY (brak instancji albo wszystkie `cancelled`) → `<SubmitForApprovalButton>`.
 *   - ŻYWA instancja (active/suspended) → stepper + `<ApprovalActionsPanel>` (gdy akceptant)
 *     + `<CancelInstanceButton>` (anuluj).
 *   - ZAKOŃCZONA non-cancelled (completed/error) → stepper + `<CancelInstanceButton>`
 *     (unieważnij = reset slotu, zwalnia → wraca „Wyślij"). BEZ „Wyślij" (slot zajęty).
 *
 * Pobiera listę instancji rekordu (klucz wspólny z `useActiveInstanceForRecord`, więc
 * react-query deduplikuje) + detal właściwej instancji (żywa, inaczej najnowsza
 * non-cancelled). Mutacje dzieci invalidują `['processes']` → baner sam się odświeża.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import {
  instanceDetailKey,
  isInstanceDetailGone,
  recordInstancesKey,
  resolveRecordProcessSlots,
  retryInstanceDetailQuery,
  selectRecordInstanceRows,
} from '@/hooks/useActiveInstanceForRecord';
import type { DecisionGate } from '@/hooks/useSaveBeforeDecision';
import { fetchInstanceDetail, fetchInstancesForRecord } from '@/lib/processes/instance-api';
import { finalNodeLabelOrNull } from '@/lib/processes/step-tone';

import { ApprovalActionsPanel } from './ApprovalActionsPanel';
import { ApprovalProgressMini } from './ApprovalProgressMini';
import { CancelInstanceButton } from './CancelInstanceButton';
import { SubmitForApprovalButton } from './SubmitForApprovalButton';

export interface ProcessApprovalBannerProps {
  moduleCode: string;
  recordId: string;
  testIdPrefix?: string;
  /** F17/klasyfikatory — typ dokumentu bieżącego rekordu (oś `crm_core.documents`),
   *  przekazywany dalej do `SubmitForApprovalButton`. */
  documentTypeId?: string | null;
  /** Kategoria bieżącego rekordu (oś kategorii — pozostałe moduły). */
  categoryId?: string | null;
  /** Bramka przed otwarciem okna decyzji (formularz w edycji: najpierw zapis, potem decyzja) —
   *  przekazywana do `ApprovalActionsPanel` ORAZ do `SubmitForApprovalButton` (panel decyzji i
   *  „Wyślij do akceptacji" nigdy nie są naraz, więc to ta sama bramka); brak = okno otwiera się
   *  od razu. Niesie też flagi wyglądu `saving` / `waiting` (blokada przycisków na czas zapisu,
   *  kręciołek na klikniętym). */
  beforeDecision?: DecisionGate;
}

export function ProcessApprovalBanner({
  moduleCode,
  recordId,
  testIdPrefix = 'process-banner',
  documentTypeId,
  categoryId,
  beforeDecision,
}: ProcessApprovalBannerProps): ReactElement | null {
  const { t } = useTranslation();

  const listQuery = useQuery({
    queryKey: recordInstancesKey(moduleCode, recordId),
    queryFn: () => fetchInstancesForRecord(moduleCode, recordId),
    enabled: !!moduleCode && !!recordId,
    select: selectRecordInstanceRows,
  });

  const list = listQuery.data ?? [];
  // Slot wolny = brak instancji o statusie ≠ cancelled (T17.1.8b); instancja do pokazania: żywa,
  // inaczej najnowsza non-cancelled (do steppera + resetu). Reguła wspólna z historią.
  const { live, relevant, free } = resolveRecordProcessSlots(list);
  const hasLive = live !== null;

  const detailQuery = useQuery({
    queryKey: instanceDetailKey(relevant?.id ?? '__none__'),
    queryFn: () => fetchInstanceDetail(relevant!.id),
    enabled: relevant !== null,
    // Ta sama reguła ponowień co hak, historia i okno z listy (bez ponowień stanów końcowych).
    retry: retryInstanceDetailQuery,
  });
  // 404 `INSTANCE_NOT_FOUND` (patrz `isInstanceDetailGone`): brak żywej sprawy (lista odświeża się
  // sama po decyzji), nie awaria — bez komunikatu błędu i bez starych danych z pamięci podręcznej.
  const detailGone = isInstanceDetailGone(detailQuery);
  const detail = relevant !== null && !detailGone ? (detailQuery.data ?? null) : null;

  if (listQuery.isPending) return null;

  // Błąd listy → zawsze blokuje (nie znamy stanu). Błąd detalu blokuje TYLKO przy żywej
  // instancji (potrzebny do akcji); dla zakończonej brak detalu = brak steppera, ale
  // przycisk resetu (z danych listy) i tak działa — nie blokujemy. Wyjątek: sprawa niedostępna
  // (`detailGone`, patrz wyżej) — to nie błąd.
  if (listQuery.isError || (hasLive && detailQuery.isError && !detailGone)) {
    return (
      <section
        data-testid={testIdPrefix}
        className="rounded border border-red-500/40 bg-bg-secondary p-3"
      >
        <p role="alert" data-testid={`${testIdPrefix}-error`} className="text-sm text-red-600">
          {t('tenant.processes.banner.loadError', 'Nie udało się wczytać stanu akceptacji.')}
        </p>
      </section>
    );
  }

  const statusLabel = (status: string): string =>
    t(`tenant.processes.banner.status.${status}`, status);
  const shownStatus = detail?.instance.status ?? relevant?.status ?? null;
  // Zakończona sprawa pokazuje NAZWĘ etapu końcowego (np. „Zaakceptowano", „Odrzucono")
  // zamiast zaszytego „Zatwierdzony"; bez niej (sprawa w toku, anulowana, starsza) — status.
  // Gdy detal jest wczytany, ufamy tylko jemu: wiersz listy bywa nieświeży (np. sprawa anulowana
  // po zakończeniu — detal ma już `null`, a lista jeszcze starą nazwę) i pokazałby zły napis.
  const finalNodeLabel = finalNodeLabelOrNull(
    detail ? detail.instance.finalNodeLabel : relevant?.finalNodeLabel,
  );

  return (
    <section
      data-testid={testIdPrefix}
      className="rounded border border-border-secondary bg-bg-secondary px-3 py-2"
    >
      {/* Wąski ekran (poniżej `sm`): dwie linie — postęp u góry, przyciski pod nim (zawijane).
          Od `sm` w górę jedna linia, jak dotąd. */}
      <div
        data-testid={`${testIdPrefix}-row`}
        className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
      >
        {/* Lewa (na wąskim: górna): stepper postępu (gdy mamy detal) albo tytuł sekcji. */}
        <div className="min-w-0 sm:flex-1">
          {detail ? (
            <ApprovalProgressMini
              steps={detail.steps}
              snapshot={detail.snapshot}
              testIdPrefix={`${testIdPrefix}-steps`}
            />
          ) : (
            <h3 className="text-sm font-medium text-text-primary">
              {t('tenant.processes.banner.title', 'Akceptacje')}
            </h3>
          )}
        </div>

        {/* Prawa (na wąskim: dolna): zwarte przyciski akcji; na wąskim zawijane w kolejne linie,
            od `sm` jedna linia bez zawijania i bez kurczenia. */}
        <div
          data-testid={`${testIdPrefix}-actions`}
          className="flex flex-wrap items-center gap-2 sm:shrink-0 sm:flex-nowrap"
        >
          {shownStatus ? (
            <span
              data-testid={`${testIdPrefix}-status`}
              data-status={shownStatus}
              className="rounded bg-bg-tertiary px-2 py-0.5 text-xs text-text-secondary"
            >
              {finalNodeLabel ?? statusLabel(shownStatus)}
            </span>
          ) : null}

          {hasLive ? (
            <ApprovalActionsPanel
              moduleCode={moduleCode}
              recordId={recordId}
              beforeDecision={beforeDecision}
            />
          ) : null}

          {relevant && !free ? (
            <CancelInstanceButton
              moduleCode={moduleCode}
              recordId={recordId}
              instanceId={relevant.id}
              instanceStatus={relevant.status}
            />
          ) : null}

          {free ? (
            <SubmitForApprovalButton
              moduleCode={moduleCode}
              recordId={recordId}
              documentTypeId={documentTypeId}
              categoryId={categoryId}
              beforeSubmit={beforeDecision}
            />
          ) : null}
        </div>
      </div>
    </section>
  );
}
